// Three-source image picker used for the story hero and inline body
// images. Sources: paste a direct link, upload from device (compressed
// + stored in R2), or search Unsplash (downloaded + compressed +
// stored in R2). All three converge on `{url, alt, caption}`.

import { useEffect, useRef, useState } from 'react';
import { Link2, Upload, Search, Loader2, X } from 'lucide-react';
import type { UnsplashSearchPhoto, UnsplashSearchResponse, UnsplashImportResponse, UploadedImage } from '@shared/types';

export interface ImageValue {
  url: string;
  alt: string;
  caption?: string;
}

type Mode = 'link' | 'upload' | 'unsplash';

export function ImagePicker({
  value,
  onChange,
  draftId,
  showCaption = true,
}: {
  value: ImageValue;
  onChange: (next: ImageValue) => void;
  draftId: string;
  showCaption?: boolean;
}) {
  const [mode, setMode] = useState<Mode>('link');

  return (
    <div className="border border-slate-200 rounded-md bg-white">
      <div className="flex gap-1 p-1 border-b border-slate-200 bg-slate-50">
        <ModeTab active={mode === 'link'} onClick={() => setMode('link')} icon={<Link2 size={12} />} label="Link" />
        <ModeTab active={mode === 'upload'} onClick={() => setMode('upload')} icon={<Upload size={12} />} label="Upload" />
        <ModeTab active={mode === 'unsplash'} onClick={() => setMode('unsplash')} icon={<Search size={12} />} label="Unsplash" />
      </div>
      <div className="p-3">
        {mode === 'link' && (
          <input
            type="text"
            value={value.url || ''}
            onChange={(e) => onChange({ ...value, url: e.target.value })}
            placeholder="https://…"
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 bg-white"
          />
        )}
        {mode === 'upload' && <UploadPanel value={value} onChange={onChange} draftId={draftId} />}
        {mode === 'unsplash' && <UnsplashPanel value={value} onChange={onChange} draftId={draftId} />}

        {value.url && (
          <div className="mt-3 flex items-start gap-3">
            <img
              src={value.url}
              alt=""
              className="w-20 h-20 object-cover rounded border border-slate-200 flex-shrink-0"
              onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
            />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-slate-500 truncate" title={value.url}>{value.url}</div>
              <button
                onClick={() => onChange({ url: '', alt: value.alt, caption: value.caption })}
                className="text-xs text-red-600 hover:underline mt-1 inline-flex items-center gap-1"
              >
                <X size={11} /> Clear
              </button>
            </div>
          </div>
        )}

        <div className="mt-3 space-y-2">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Alt text</label>
            <input
              type="text"
              value={value.alt || ''}
              onChange={(e) => onChange({ ...value, alt: e.target.value })}
              placeholder="Brief description for screen readers"
              className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded bg-white"
            />
          </div>
          {showCaption && (
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Caption / credit</label>
              <input
                type="text"
                value={value.caption || ''}
                onChange={(e) => onChange({ ...value, caption: e.target.value })}
                placeholder="Photo: Source"
                className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded bg-white"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ModeTab({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 px-2 py-1.5 text-xs rounded inline-flex items-center justify-center gap-1.5 transition-colors ${
        active ? 'bg-white text-blue-700 shadow-sm border border-slate-200' : 'text-slate-600 hover:bg-white/60'
      }`}
    >
      {icon} {label}
    </button>
  );
}

// ============================================================
// Upload panel
// ============================================================
function UploadPanel({ value, onChange, draftId }: { value: ImageValue; onChange: (v: ImageValue) => void; draftId: string }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  async function uploadFile(file: File) {
    setError(null);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('draftId', draftId);
      fd.append('file', file);
      const res = await fetch('/api/images/upload', { method: 'POST', body: fd, credentials: 'include' });
      const json = (await res.json()) as UploadedImage & { error?: string };
      if (!res.ok) throw new Error(json.error || `Upload failed (${res.status})`);
      onChange({ ...value, url: json.url });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file) uploadFile(file);
        }}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer border-2 border-dashed rounded-md p-4 text-center transition-colors ${
          dragOver ? 'border-blue-400 bg-blue-50' : 'border-slate-300 hover:border-slate-400 bg-slate-50'
        }`}
      >
        {busy ? (
          <div className="inline-flex items-center gap-2 text-xs text-slate-600">
            <Loader2 size={14} className="animate-spin" /> Uploading…
          </div>
        ) : (
          <>
            <Upload size={16} className="mx-auto text-slate-400 mb-1" />
            <div className="text-xs text-slate-600">Drag & drop, or click to choose</div>
            <div className="text-[10px] text-slate-400 mt-0.5">JPEG, PNG, WebP, GIF · up to 10 MB</div>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) uploadFile(file);
          if (inputRef.current) inputRef.current.value = '';
        }}
      />
      {error && <div className="text-xs text-red-600 mt-2">{error}</div>}
    </div>
  );
}

// ============================================================
// Unsplash panel
// ============================================================
function UnsplashPanel({ onChange, draftId }: { value: ImageValue; onChange: (v: ImageValue) => void; draftId: string }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UnsplashSearchPhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Debounced search
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      return;
    }
    setLoading(true);
    setError(null);
    const handle = setTimeout(async () => {
      try {
        const res = await fetch(`/api/images/unsplash/search?q=${encodeURIComponent(q)}`, { credentials: 'include' });
        const json = (await res.json()) as UnsplashSearchResponse & { error?: string };
        if (!res.ok) throw new Error(json.error || `Search failed (${res.status})`);
        setResults(json.results);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Search failed');
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  async function importPhoto(photo: UnsplashSearchPhoto) {
    setImporting(photo.id);
    setError(null);
    try {
      const res = await fetch('/api/images/unsplash/import', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ draftId, photoId: photo.id }),
      });
      const json = (await res.json()) as UnsplashImportResponse & { error?: string };
      if (!res.ok) throw new Error(json.error || `Import failed (${res.status})`);
      onChange({ url: json.url, alt: json.alt, caption: json.caption });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setImporting(null);
    }
  }

  return (
    <div>
      <div className="relative">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search Unsplash…"
          className="w-full pl-7 pr-3 py-1.5 text-xs border border-slate-200 rounded bg-white"
        />
        {loading && <Loader2 size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
      </div>
      {error && <div className="text-xs text-red-600 mt-2">{error}</div>}
      {results.length > 0 && (
        <div className="grid grid-cols-3 gap-1.5 mt-2 max-h-64 overflow-y-auto">
          {results.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => importPhoto(p)}
              disabled={importing !== null}
              className="relative group block rounded overflow-hidden border border-slate-200 hover:border-blue-400 disabled:opacity-50"
              title={`Photo by ${p.user.name}`}
            >
              <img src={p.urls.thumb} alt={p.alt_description || ''} className="w-full h-20 object-cover" loading="lazy" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent text-white text-[9px] px-1 py-0.5 truncate">
                {p.user.name}
              </div>
              {importing === p.id && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <Loader2 size={16} className="text-white animate-spin" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}
      <div className="text-[10px] text-slate-400 mt-2">
        Photos hosted in our R2 bucket; photographer credit auto-fills the caption.
      </div>
    </div>
  );
}
